/**
 * Domain types mirroring windocs-backend/src/core/types/domain.ts.
 * Keep the two in step - they are the contract between the repos.
 */

export const UserRole = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  TENANT_ADMIN: 'TENANT_ADMIN',
  TENANT_CLIENT: 'TENANT_CLIENT',
  /**
   * A customer watching one job, and nothing else.
   *
   * The only role in this product held by somebody outside the firm. They hold
   * a tenantId like everybody else, so nothing can be inferred from its
   * absence - what pens them in is that the tenant API names the two roles it
   * serves and refuses the rest, and that their grants resolve to NONE on
   * every subject. The router keeps them off every app route for the same
   * reason: the pages would render empty and then 403 one request at a time.
   */
  JOB_GUEST: 'JOB_GUEST',
} as const;

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const UserStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  LOCKED: 'LOCKED',
} as const;

export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

export const TenantStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;

export type TenantStatus = (typeof TenantStatus)[keyof typeof TenantStatus];

/** Includes EXPIRED/EXPIRING_SOON, which the server derives from the date. */
export type TenantDisplayStatus = 'ACTIVE' | 'INACTIVE' | 'EXPIRED' | 'EXPIRING_SOON';

/**
 * What a tenant's people may reach, and how far in.
 *
 * Mirrors `core/types/permissions.ts` on the server, which is the list the
 * code enforces. These are here so the UI can hide what it must not offer -
 * a menu entry nobody may open, a Delete that will be refused. Hiding is
 * manners; the server decides.
 */
export const PermissionSubject = {
  CUSTOMERS: 'customers',
  LEADS: 'leads',
  BILLING: 'billing',
  JOBS: 'jobs',
  JOB_BOOKING: 'job_booking',
  DOCUMENTS: 'documents',
  EMPLOYEES: 'employees',
  LEAVE: 'leave',
  PAYROLL: 'payroll',
  ACCOUNTS: 'accounts',
  SETTINGS: 'settings',
  AUDIT: 'audit',
  ACCESS: 'access',
} as const;

export type PermissionSubject = (typeof PermissionSubject)[keyof typeof PermissionSubject];

export const AccessLevel = {
  NONE: 'none',
  READ: 'read',
  WRITE: 'write',
} as const;

export type AccessLevel = (typeof AccessLevel)[keyof typeof AccessLevel];

/** Subject to level. A subject the server has not sent is simply not held. */
export type Grants = Partial<Record<PermissionSubject, AccessLevel>>;

/** One thing access can be given to, as the server describes it. */
export interface PermissionSubjectDefinition {
  subject: PermissionSubject;
  label: string;
  description: string;
  fallback: AccessLevel;
  /** The levels worth offering - some subjects are on or off. */
  levels: AccessLevel[];
}

/** A named set of permissions a tenant assigns to their people. */
export interface AccessProfile {
  id: string;
  key: string;
  label: string;
  description: string;
  grants: Grants;
  isSystem: boolean;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
  /** How many sign-ins are on it. */
  inUseCount: number;
}

export interface SessionUser {
  id: string;
  username: string;
  email: string;
  name: string;
  role: UserRole;
  tenantId: string | null;
  tenantName: string | null;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  /** Resolved by the server. An administrator holds everything. */
  permissions: Grants;
}

export interface Tenant {
  id: string;
  tenantCode: string;
  name: string;
  industryId: string | null;
  industryName: string | null;
  tradeLicenseNo: string;
  subscriptionExpiresAt: string;
  maxAdminUsers: number;
  maxClientUsers: number;
  address: string;
  remarks: string;
  status: TenantStatus;
  displayStatus: TenantDisplayStatus;
  adminUserCount: number;
  clientUserCount: number;
  documentCount: number;
  createdAt: string;
  updatedAt: string;
}

/** The list endpoint omits the detail-only fields. */
export type TenantListRow = Omit<
  Tenant,
  'industryId' | 'address' | 'remarks' | 'documentCount' | 'updatedAt'
>;

export interface AppUser {
  id: string;
  username: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  tenantId: string | null;
  tenantName: string | null;
  tenantCode: string | null;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

/**
 * A tenant's industry - Auditors, Accounting, DPMS and so on.
 * Named `Industry` because that is what the tenant form calls the field.
 */
export interface Industry {
  id: string;
  name: string;
  isActive: boolean;
  tenantCount: number;
  createdAt: string;
}

export interface AuditChange {
  field: string;
  from: unknown;
  to: unknown;
}

export interface AuditLogEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  tenantId: string | null;
  tenantName: string | null;
  tenantCode: string | null;
  actorUsername: string;
  actorRole: string;
  summary: string;
  changes: AuditChange[];
  ipAddress: string | null;
  createdAt: string;
}

/**
 * How a file list is ordered.
 *
 * `LABEL` groups by the file's first label alphabetically, which is what
 * somebody sorting by label is asking for.
 */
export const DocumentSort = {
  RECENT: 'recent',
  OLDEST: 'oldest',
  NAME: 'name',
  SIZE: 'size',
  LABEL: 'label',
} as const;

export type DocumentSort = (typeof DocumentSort)[keyof typeof DocumentSort];

/** One label from the tenant's master, with how many files carry it. */
export interface DocumentLabelOption {
  id: string;
  key: string;
  label: string;
  colour: AccentColour;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
  /** How many documents carry it, across everything. */
  inUseCount: number;
}

/** What a folder holds, whether the folder is a customer or a label. */
export interface FolderTotals {
  fileCount: number;
  totalBytes: number;
  lastUploadedAt: string | null;
}

/**
 * One label, seen as a folder.
 *
 * The counts across labels deliberately overlap, because a file can carry
 * several - the sum is not the number of files.
 */
export interface CustomerFileLabel extends DocumentLabelOption, FolderTotals {}

export interface CustomerFileLabels {
  items: CustomerFileLabel[];
  /**
   * What carries no label at all. Not a label of its own: it has no row in
   * the master, and a tenant who named one "Unlabelled" would collide with it.
   */
  unlabelled: FolderTotals;
}

/**
 * One customer's folder in the file explorer.
 *
 * A folder is not stored anywhere: it is the customer, plus a count of what
 * names them. Which is why an empty one still exists - it is where the first
 * file goes.
 */
export interface CustomerFolder {
  customerId: string;
  name: string;
  customerCode: string;
  kind: CustomerKind;
  status: CustomerStatus;
  fileCount: number;
  totalBytes: number;
  lastUploadedAt: string | null;
}

/**
 * A day something runs out on - a folder on the by-expiry shelf.
 *
 * Keyed by the day rather than by a document, because whoever chases renewals
 * works a diary: everything lapsing on the 21st is one errand, whether it is
 * one passport or nine. A day with no scan filed still gets a folder, since
 * "the licence goes on Friday and we hold no copy" is the useful answer.
 */
export type PaperOwnerKind = 'customer' | 'employee';

export interface ExpiryPaper {
  /** What it is: "Passport", "Trade licence". */
  label: string;
  /** Whose it is - a customer or a member of staff. */
  ownerName: string;
  ownerKind: PaperOwnerKind;
  /**
   * The company they belong to, so a day can read
   * "Passport · Yusuf Ahmed (Al Noor Trading LLC)".
   *
   * Empty for a company, for an individual nobody has filed, and for staff.
   */
  ownerParentName: string;
}

export interface ExpiryFolder {
  /** `YYYY-MM-DD`. */
  date: string;
  /** Negative once the day has passed. */
  daysLeft: number;
  fileCount: number;
  totalBytes: number;
  /** What is lapsing that day, for the folder's subtitle. */
  papers: ExpiryPaper[];
  /** How many people are affected. */
  ownerCount: number;
}

/** A file sitting loose on a record that looks like the paper lapsing. */
export interface LooseScan {
  documentId: string;
  /** What the folder shows it as. */
  name: string;
  sizeBytes: number;
  /**
   * The entry it is filed against today, or null when it hangs off nothing.
   *
   * Set means taking it is a move: the scan leaves wherever it is now, and
   * the button has to say so rather than reading "Attach".
   */
  attachedTo: string | null;
}

/**
 * An entry running out that day with nothing filed against it - and a scan of
 * it, apparently, on the same record.
 *
 * The by-expiry shelf can only see a scan through `identityDocumentId`, and
 * exactly one route in the app ever sets it: a file dropped inside an ID row
 * on the record form. "Add files", the customer Files tab and a job's Files
 * tab all send labels and leave the link null, so the day page told a
 * renewals clerk that no scan had been uploaded while the scan sat on the
 * owner's folder in plain sight - and the clerk went and chased the customer
 * for it.
 *
 * The link still means what it means; this is the second look that keeps the
 * page from asserting an absence it cannot see. The server returns a row only
 * where the entry has *no* scan attached at all and the same owner holds at
 * least one file carrying that entry's type as a label.
 */
export interface UnattachedPaper {
  /** The identity entry with nothing on it. */
  entryId: string;
  /** What it is, written out: "Emirates ID". */
  label: string;
  ownerId: string;
  ownerName: string;
  ownerKind: PaperOwnerKind;
  candidates: LooseScan[];
}

/**
 * One entry a file could be a scan of, for the picker in the edit drawer.
 *
 * `attachedCount` is here because the repair this picker exists for is moving
 * a scan off the wrong entry, and somebody doing that wants to see which
 * entry already has papers on it before they move one onto another.
 */
export interface IdentityOption {
  id: string;
  /** The key from the tenant's document-type master. */
  type: string;
  /** That key written out. */
  label: string;
  number: string;
  expiresOn: string | null;
  attachedCount: number;
}

/** A file found by searching across every folder. */
export interface CustomerFile extends TenantDocument {
  /**
   * Empty on a staff file, which has no customer. Read `owner` instead: it is
   * filled whoever the file belongs to, and says which record to open and
   * which route the file itself is served from.
   */
  customerId: string;
  customerName: string;
  customerCode: string;
  customerKind: CustomerKind;
  /** Whose file it is. Only the by-expiry shelf ever asks for staff files. */
  owner: {
    kind: PaperOwnerKind;
    id: string;
    name: string;
    /** Their reference - a customer code or an employee code. */
    code: string;
    /** Null on a company, on an individual nobody has filed, and on staff. */
    parentCorporate: { id: string; name: string; customerCode: string } | null;
    /** A key from the tenant's relationship-type master; empty when loose. */
    relationshipKey: string;
    /** That key written out, so no screen holds the master. */
    relationshipLabel: string;
  };
}

/**
 * What a scan is a scan *of*.
 *
 * A passport photocopy is not a loose file - it hangs off the passport entry
 * on somebody's record, and the number and the dates are the first things
 * anybody opening it wants.
 */
export interface DocumentIdentity {
  id: string;
  /** The key from the tenant's document-type master. */
  type: string;
  /** That type written out. */
  label: string;
  number: string;
  issuedOn: string;
  expiresOn: string | null;
}

export interface TenantDocument {
  id: string;
  /** What to show: the tenant's own name for it, or the uploaded one. */
  name: string;
  /** What arrived on the upload, whatever it has since been renamed to. */
  originalName: string;
  /** The tenant's own name, empty when nobody has renamed it. */
  displayName: string;
  /** Keys from the tenant's document-label master. */
  labels: string[];
  mimeType: string;
  sizeBytes: number;
  category: string;
  description: string;
  /** The identity document this is a scan of, or null. */
  identityDocumentId: string | null;
  /** That document's own details, when this is a scan of one. */
  identity: DocumentIdentity | null;
  /**
   * What the upload scanners concluded. Never `infected`: those are refused
   * at upload, so a document that exists was at worst flagged.
   */
  scanVerdict: 'clean' | 'suspicious';
  /** Plain-language notes behind a `suspicious` verdict; empty when clean. */
  scanFindings: string[];
  uploadedByUsername: string;
  /**
   * Where the file came from, when it was collected while working a job.
   *
   * Null for one added from the record's own page. Both the job code and the
   * stage are as they stood at the time, not a pointer at a stage that may
   * since have been renamed - so this keeps saying what was true on the day.
   */
  source: {
    jobId: string;
    jobCode: string;
    stageKey: string;
    stageLabel: string;
  } | null;
  createdAt: string;
}

export interface DashboardStats {
  tenants: {
    total: number;
    active: number;
    inactive: number;
    expired: number;
    expiringSoon: number;
    newLast30Days: number;
  };
  users: {
    total: number;
    admins: number;
    clients: number;
    superUsers: number;
    inactive: number;
    locked: number;
    pendingPasswordChange: number;
  };
  documents: {
    total: number;
    totalBytes: number;
  };
  tenantsByIndustry: { industryId: string; industryName: string; count: number }[];
  recentActivity: {
    id: string;
    action: string;
    summary: string;
    actorUsername: string;
    createdAt: string;
  }[];
}

/** Returned once on tenant creation and on a password reset. */
export interface GeneratedCredentials {
  username: string;
  password: string;
}

/* -------------------------------------------------------------------------- *
 * The tenant's own view of itself
 * -------------------------------------------------------------------------- */

export const EmployeeStatus = {
  ACTIVE: 'ACTIVE',
  /** Kept on file after they leave; their sign-in, if any, is suspended. */
  LEFT: 'LEFT',
} as const;

export type EmployeeStatus = (typeof EmployeeStatus)[keyof typeof EmployeeStatus];

/** Days before an identity document lapses at which it starts warning. */
export const DOCUMENT_EXPIRY_WARNING_DAYS = 30;

/**
 * The kinds of identity document an employee file can hold.
 *
 * Kept in step with IdentityDocumentType in the backend domain module.
 */
export const IdentityDocumentType = {
  EMIRATES_ID: 'EMIRATES_ID',
  PASSPORT: 'PASSPORT',
  /* The visa is a paper like the others. It used to be three fields on the
     customer instead, which meant no number, no scan and no place in any
     list of documents. */
  VISA: 'VISA',
  PREVIOUS_VISA: 'PREVIOUS_VISA',
  GCC_ID: 'GCC_ID',
  GOVT_APPROVED_ID: 'GOVT_APPROVED_ID',
} as const;

export type IdentityDocumentType = (typeof IdentityDocumentType)[keyof typeof IdentityDocumentType];

export const IDENTITY_DOCUMENT_TYPES = Object.values(IdentityDocumentType);

/** How each type is written for a person to read. */
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  EMIRATES_ID: 'Emirates ID',
  PASSPORT: 'Passport',
  GCC_ID: 'GCC ID',
  GOVT_APPROVED_ID: 'Govt Approved ID',
  TRADE_LICENCE: 'Trade Licence',
  CERTIFICATE_OF_INCORPORATION: 'Certificate of Incorporation',
  MEMORANDUM_OF_ASSOCIATION: 'Memorandum of Association',
  VAT_CERTIFICATE: 'VAT Certificate',
  ESTABLISHMENT_CARD: 'Establishment Card',
  OTHER_COMPANY_DOCUMENT: 'Other company document',
};

/**
 * How a document type is written for a person to read.
 *
 * Falls back to the raw value rather than an empty label: a type the client
 * has not heard of yet - one added to the API ahead of a deploy - should read
 * oddly, not vanish.
 */
export function documentTypeLabel(type: string): string {
  return DOCUMENT_TYPE_LABELS[type] ?? type;
}

/** The registration documents a company is identified by. */
export const CompanyDocumentType = {
  TRADE_LICENCE: 'TRADE_LICENCE',
  CERTIFICATE_OF_INCORPORATION: 'CERTIFICATE_OF_INCORPORATION',
  MEMORANDUM_OF_ASSOCIATION: 'MEMORANDUM_OF_ASSOCIATION',
  VAT_CERTIFICATE: 'VAT_CERTIFICATE',
  ESTABLISHMENT_CARD: 'ESTABLISHMENT_CARD',
  OTHER_COMPANY_DOCUMENT: 'OTHER_COMPANY_DOCUMENT',
} as const;

export type CompanyDocumentType = (typeof CompanyDocumentType)[keyof typeof CompanyDocumentType];

export const COMPANY_DOCUMENT_TYPES = Object.values(CompanyDocumentType);

/** Every kind of identifying document, for a person or a company. */
export type RegistryDocumentType = IdentityDocumentType | CompanyDocumentType;

/** One identity document on an employee's file. */
export interface IdentityDocument {
  id: string;
  type: IdentityDocumentType;
  /** The type written out, so the UI never owns the mapping. */
  label: string;
  number: string;
  /**
   * Nullable, which it was not before.
   *
   * A staff visa usually has neither a number nor an issue date, and the
   * endpoint stopped requiring them when staff papers were brought in line with
   * a customer's. Anything reading this has to cope with null - `identityRowsFrom`
   * called `.slice()` on it straight and would have thrown.
   */
  issuedOn: string | null;
  expiresOn: string | null;
  /** A key from the tenant's visa-status master. Empty when the paper has none. */
  status: string;
  /** That key written out, so no screen holds the master. */
  statusLabel: string;
  /** False only where the status master says that position does not lapse. */
  expires: boolean;
  remarks: string;
  /** Scans attached to this particular ID. */
  fileCount: number;
}

/** An identity document lapsing inside the warning window, or already lapsed. */
export interface ExpiringIdentityDocument {
  id: string;
  type: IdentityDocumentType;
  label: string;
  on: string;
  /** Negative once it has lapsed - "renew soon" versus "should have renewed". */
  daysLeft: number;
}

/**
 * A person who works for the tenant.
 *
 * `login` is null for most of them, and that is the point: an employee record
 * is a file the organisation keeps, while a login is permission to use the
 * software. The two are granted, revoked and counted separately.
 */
/**
 * One member of staff as a picker sees them.
 *
 * The twin of `CustomerLookupRow`. The full `Employee` carries documents,
 * expiry windows and a sign-in, which is the right price for a page somebody
 * asked for and the wrong one for a query that refires while they type.
 */
export interface EmployeeLookupRow {
  id: string;
  name: string;
  employeeCode: string;
  designation: string;
  status: EmployeeStatus;
  hasLogin: boolean;
}

export interface Employee {
  id: string;
  employeeCode: string;
  name: string;
  designation: string;
  department: string;
  email: string;
  phone: string;
  joinedOn: string;
  /** Their last day, or null while they are still here. */
  leftOn: string | null;
  status: EmployeeStatus;
  nationality: string;
  identityDocuments: IdentityDocument[];
  login: {
    userId: string;
    username: string;
    status: UserStatus;
    mustChangePassword: boolean;
    lastLoginAt: string | null;
    /** Null means the tenant's default, which is where everybody starts. */
    accessProfileId: string | null;
    accessProfileLabel: string | null;
    /** An administrator is on no profile: they hold everything regardless. */
    role: UserRole;
  } | null;
  documentCount: number;
  expiring: ExpiringIdentityDocument[];
  createdAt: string;
}

/**
 * What a tenant sees about their own organisation.
 *
 * A narrower thing than the superuser's tenant record: the seat caps are here
 * because an admin needs them to know whether they can give one more person
 * access, but none of the platform's commercial fields are.
 */
export interface WorkspaceOverview {
  id: string;
  name: string;
  tenantCode: string;
  tradeLicenseNo: string;
  address: string;
  subscriptionExpiresAt: string;
  employees: {
    total: number;
    active: number;
    withLogin: number;
    /** Active staff with a passport or visa lapsing within 30 days. */
    expiringSoon: number;
  };
  signIns: {
    admins: number;
    employees: number;
    active: number;
    maxAdmins: number;
    maxEmployeeSignIns: number;
    seatsLeft: number;
  };
  customers: {
    total: number;
    individuals: number;
    corporates: number;
    active: number;
    /** Licences and IDs lapsing inside the warning window. */
    expiringSoon: number;
  };
  documents: {
    employee: number;
    customer: number;
    total: number;
    storageUsedBytes: number;
  };
  /** Papers running out, counted two ways. See ExpiryBreakdown. */
  expiries: ExpiryBreakdown;
  /**
   * The tenant's work, narrowed to whoever is reading - an administrator sees
   * the whole board, everyone else their own - so these figures and the jobs
   * page can never disagree.
   */
  jobs: JobOverview;
  /**
   * The pipeline, in two figures.
   *
   * Null unless the reader may read leads - the subject ships closed, so for
   * most firms the tile is simply not drawn.
   */
  leads: { open: number; followUpDue: number } | null;
  /**
   * What customers owe. Null unless the reader may read billing - that
   * subject ships closed too, and what the firm is owed says what the
   * business is worth this month.
   */
  receivables: {
    currency: string;
    outstandingMinor: number;
    overdueMinor: number;
    count: number;
  } | null;
  /** What lapses soonest, across staff and customers. */
  renewals: Renewal[];
  /**
   * People and money, for an administrator only.
   *
   * Null for an ordinary employee: the server does not compute these for them
   * and does not send the company's takings to a client with no business
   * rendering them. See HrSnapshot, declared with the accounts types below.
   */
  hr: HrSnapshot | null;
}

/**
 * Everything running out in the next 90 days, cut two ways.
 *
 * Counted per paper rather than per person: someone whose passport and visa
 * both lapse is two things to do, and a count of people would say one. A
 * status whose master row says it does not expire - a cancelled visa - is
 * left out, the same way it is left out of the renewals list, because its
 * date has already done its job.
 */
export interface ExpiryBreakdown {
  /** Whose paper it is. The three are exclusive and sum to `total`. */
  byOwner: { employee: number; individual: number; corporate: number };
  /** How soon. Also exclusive, and also summing to `total`. */
  byUrgency: { lapsed: number; within30: number; within90: number };
  total: number;
}

/** How much live work is sitting at each stage. */
export interface StageTally {
  flowId: string;
  key: string;
  label: string;
  colour: AccentColour;
  count: number;
  /** Which service's flow, so two stages of the same name can be told apart. */
  flowName: string;
}

export interface JobOverview {
  total: number;
  open: number;
  overdue: number;
  dueSoon: number;
  closed: number;
  /** Open work per stage, busiest first - where everything is piling up. */
  byStage: StageTally[];
  /** What is closest to its date, overdue first. */
  dueNext: Job[];
}

/* -------------------------------------------------------------------------- *
 * Customers
 * -------------------------------------------------------------------------- */

/**
 * Who the tenant does business with.
 *
 * The split runs through the whole feature: a person is identified by a
 * passport, a company by a trade licence, and the two are onboarded on
 * separate screens because they ask for different things.
 */
export const CustomerKind = {
  /** A natural person. B2C. */
  INDIVIDUAL: 'INDIVIDUAL',
  /** A company. B2B. */
  CORPORATE: 'CORPORATE',
} as const;

export type CustomerKind = (typeof CustomerKind)[keyof typeof CustomerKind];

export const CustomerStatus = {
  ACTIVE: 'ACTIVE',
  /** Kept on file, no longer trading with. */
  INACTIVE: 'INACTIVE',
} as const;

export type CustomerStatus = (typeof CustomerStatus)[keyof typeof CustomerStatus];

/**
 * How a company is licensed, which in the UAE is one of exactly two answers.
 *
 * Fixed rather than a tenant master, unlike nearly everything else a firm
 * names for itself: this is federal law, not a preference, and a firm renaming
 * Mainland would change what the record means rather than how it reads.
 *
 * Empty is a third state and a real one - a company onboarded before the
 * question was asked has no answer, and the server falls back to whatever was
 * typed into the free-text box this replaced.
 */
export const JurisdictionType = {
  MAINLAND: 'MAINLAND',
  FREEZONE: 'FREEZONE',
} as const;

export type JurisdictionType = (typeof JurisdictionType)[keyof typeof JurisdictionType];

/** For the dropdown, in the order they are offered. */
export const JURISDICTION_TYPE_OPTIONS = [
  { value: JurisdictionType.MAINLAND, label: 'Mainland' },
  { value: JurisdictionType.FREEZONE, label: 'Freezone' },
] as const;

/**
 * The seven emirates, in the order they are conventionally listed.
 *
 * Kept in step with `EMIRATES` in the backend's `core/types/domain.ts`, which
 * is what the stored key is validated against. A key rather than the name so
 * the spelling of "Umm Al Quwain" is decided in one place.
 */
export const EMIRATE_OPTIONS = [
  { value: 'ABU_DHABI', label: 'Abu Dhabi' },
  { value: 'DUBAI', label: 'Dubai' },
  { value: 'SHARJAH', label: 'Sharjah' },
  { value: 'AJMAN', label: 'Ajman' },
  { value: 'UMM_AL_QUWAIN', label: 'Umm Al Quwain' },
  { value: 'RAS_AL_KHAIMAH', label: 'Ras Al Khaimah' },
  { value: 'FUJAIRAH', label: 'Fujairah' },
] as const;

export type Emirate = (typeof EMIRATE_OPTIONS)[number]['value'];

/**
 * Where a document type may be used.
 *
 * A passport applies to staff and to individual customers alike; a trade
 * licence only to companies. Each tenant's master list marks every type with
 * the places it belongs.
 */
export const DocumentTypeScope = {
  EMPLOYEE: 'EMPLOYEE',
  CUSTOMER_INDIVIDUAL: 'CUSTOMER_INDIVIDUAL',
  CUSTOMER_CORPORATE: 'CUSTOMER_CORPORATE',
} as const;

export type DocumentTypeScope = (typeof DocumentTypeScope)[keyof typeof DocumentTypeScope];

export const DOCUMENT_TYPE_SCOPES = Object.values(DocumentTypeScope);

/** Which scope a customer of this kind uses. */
export function documentScopeForCustomer(kind: CustomerKind): DocumentTypeScope {
  return kind === CustomerKind.CORPORATE
    ? DocumentTypeScope.CUSTOMER_CORPORATE
    : DocumentTypeScope.CUSTOMER_INDIVIDUAL;
}

/**
 * A customer as a picker needs them, and no more.
 *
 * Deliberately not a whole `Customer`: this comes back while somebody is
 * still typing a name, so it carries who they are and whose they already
 * are, and nothing that has to be assembled.
 */
export interface CustomerLookupRow {
  id: string;
  name: string;
  customerCode: string;
  kind: CustomerKind;
  /** So a picker can say "currently under Gulf Metals" before they choose. */
  parentCorporate: { id: string; name: string; customerCode: string } | null;
}

/**
 * One of a company's members, as its roster shows them.
 *
 * Deliberately not a whole `Customer`. Assembling one costs two aggregations
 * and four master lookups, and a roster of forty would pay that forty times
 * over to draw a table of names. Opening somebody is the peek drawer, which
 * fetches the rest at the point it is actually wanted.
 */
/** One company a customer belongs to, and what they are to it. */
export interface MemberOf {
  membershipId: string;
  company: { id: string; name: string; customerCode: string };
  relationshipKey: string;
  /** That key written out by the server, so no screen holds the master. */
  relationshipLabel: string;
}

export interface CorporatePerson {
  id: string;
  name: string;
  customerCode: string;
  /**
   * Whether this member is a person or another company.
   *
   * A holding company is a shareholder in its subsidiaries, so a roster is no
   * longer a list of people - and a row that does not say which it is reads as
   * a person with a company's name.
   */
  kind: CustomerKind;
  /**
   * Their place in *this* company, which is what a change or a removal names.
   *
   * Distinct from `id` above: that says who they are, this says where they
   * stand here. The same member in two companies has one id and two of these.
   */
  membershipId: string;
  email: string;
  status: CustomerStatus;
  /**
   * A key from the tenant's relationship-type master; empty when unstated.
   *
   * A fact about the membership rather than about the member: the same company
   * is a shareholder in one firm and a holding company in another.
   */
  relationshipKey: string;
  relationshipLabel: string;
  /** Their own folder, not the company's. */
  documentCount: number;
  /** Negative `daysLeft` once it has lapsed. Null when nothing is dated. */
  soonestExpiry: { on: string; daysLeft: number } | null;
}

/**
 * One row of "who belongs to which company".
 *
 * What the roster's write endpoints answer with. Membership is a record in its
 * own right because one member may belong to several companies and be a
 * different thing to each, so it is the membership - not the member - that
 * carries the standing and that a change or a removal names.
 */
export interface CustomerMembership {
  id: string;
  companyId: string;
  memberId: string;
  relationshipKey: string;
}

/**
 * One entry in a tenant's relationship-type master.
 *
 * How a person is connected to the company they sit under: its shareholder,
 * its manager, somebody on its sponsorship. A master like every other list
 * here, because the words differ by firm, by emirate and by free zone.
 */
/**
 * Which half of a company's people a standing belongs to.
 *
 * On the tenant's master rather than decided here, which is the point: a firm
 * can add standings of their own, and a list of keys hardcoded in this app would
 * drop every one of them into whichever section happened to be the fallback -
 * including, on a bad day, among the owners of a company.
 */
export const RelationshipSide = {
  /** A stake in the company: shareholders, partners, investors. */
  INVESTOR: 'INVESTOR',
  /** Someone the company appoints or sponsors: managers, signatories, staff. */
  EMPLOYEE: 'EMPLOYEE',
} as const;

export type RelationshipSide = (typeof RelationshipSide)[keyof typeof RelationshipSide];

export interface RelationshipTypeMaster {
  id: string;
  /** Stable. What records store, and what a rename does not touch. */
  key: string;
  label: string;
  description: string;
  /** Drawn by `accentClasses`, so a standing is one colour wherever it shows. */
  colour: AccentColour;
  /** Which of the People panel's two sections this standing falls in. */
  side: RelationshipSide;
  isActive: boolean;
  /** Seeded with the tenant; renameable and deactivatable, not deletable. */
  isSystem: boolean;
  sortOrder: number;
  /** How many people are recorded as this, so a warning can be shown. */
  inUseCount: number;
}

/** One entry in a tenant's document-type master. */
export interface DocumentTypeMaster {
  id: string;
  /** Stable. What records store, and what a rename does not touch. */
  key: string;
  label: string;
  appliesTo: DocumentTypeScope[];
  isActive: boolean;
  /** Seeded with the tenant; renameable and deactivatable, not deletable. */
  isSystem: boolean;
  sortOrder: number;
  /**
   * Records of this type also carry a status from the visa-status master.
   *
   * A flag on the master rather than a hardcoded list of visa keys, so a
   * tenant who adds their own "Golden visa" can say it behaves the same way.
   */
  tracksStatus: boolean;
  /** What the expiry is called here; empty for the ordinary wording. */
  expiryLabel: string;
  /** How many records use it, so a warning can be shown before retiring one. */
  inUseCount: number;
}

/** One document somewhere in the tenant that needs renewing. */
export interface Renewal {
  ownerType: 'EMPLOYEE' | 'CUSTOMER';
  /** For a customer, which sort; null for staff. */
  ownerKind: CustomerKind | null;
  ownerId: string;
  ownerName: string;
  ownerCode: string;
  documentId: string;
  label: string;
  expiresOn: string;
  /** Negative once it has lapsed. */
  daysLeft: number;
}

/** A document that identifies a customer: a passport, or a trade licence. */
export interface RegistryDocument {
  id: string;
  type: RegistryDocumentType;
  label: string;
  /**
   * Both may be empty: a visa moved over from the fields it used to live in
   * has neither, and inventing one would read as real. See the subdocument
   * in the backend's customer.model.ts.
   */
  number: string;
  issuedOn: string | null;
  expiresOn: string | null;
  /** A visa-status key, on the types that carry one. Empty otherwise. */
  status: string;
  /** That key written out by the server, so no screen holds the master. */
  statusLabel: string;
  /**
   * Whether this paper's date is still worth chasing.
   *
   * False only for a status whose master row says it does not expire - a
   * cancelled visa carries a date that has already done its job. Sent by the
   * server because no screen holds the status master, only the key and the
   * label: without it the header correctly said nothing was due while the
   * gauge beneath it counted the same visa down in amber.
   */
  expires: boolean;
  /** Anything worth saying about this particular paper. */
  remarks: string;
  fileCount: number;
}

/**
 * One entry in the tenant's visa-status master.
 *
 * The list is theirs, so nothing here is a fixed set of values - a status is
 * whatever they defined, and `expires` is their answer to "does a visa in this
 * position have an expiry worth chasing?".
 */
export interface VisaStatusOption {
  id: string;
  key: string;
  label: string;
  expires: boolean;
  isDefault: boolean;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
  /** How many customers hold it. */
  inUseCount: number;
}

/**
 * One country on the tenant's own list.
 *
 * Seeded from the standard list and theirs from then on - a firm that only
 * ever sees a dozen nationalities should be able to say so. The key is the
 * ISO alpha-3 code, which is what records store, so renaming "Holland" to
 * "Netherlands" relabels every customer that holds it.
 */
export interface CountryOption {
  id: string;
  /** ISO alpha-3 - ARE, IND, GBR. */
  key: string;
  label: string;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
  /** How many customers hold it. */
  inUseCount: number;
}

export interface Customer {
  id: string;
  customerCode: string;
  kind: CustomerKind;
  name: string;
  email: string;
  phone: string;
  /** Held apart from the phone: in this market they often differ. */
  whatsapp: string;
  /** Their tax registration number. Blank on an individual, which is normal. */
  trn: string;
  address: string;
  onboardedOn: string;
  status: CustomerStatus;
  notes: string;
  /** Individuals only; empty on a corporate. An ISO alpha-3 country key. */
  nationality: string;
  /** That key written out by the server, so no screen holds the master. */
  nationalityLabel: string;
  dateOfBirth: string | null;
  /* The visa used to be three fields here. It is an ordinary document now,
     carried in `registryDocuments` with a status of its own. */

  /**
   * The one company the customer form asks about, or null.
   *
   * Named rather than just identified, so a screen can say whose they are
   * without a second request.
   *
   * No longer the whole truth: membership is many-to-many now, and somebody
   * added through a company's roster rather than through this field comes back
   * here as null while plainly being on that roster. It is still what the form
   * writes and what the server keeps in step with the roster, so it stays -
   * but a screen that needs every company a customer belongs to cannot get
   * them from here.
   */
  parentCorporate: { id: string; name: string; customerCode: string } | null;
  /** A key from the tenant's relationship-type master. Empty when loose. */
  relationshipKey: string;
  /** That key written out by the server, so no screen holds the master. */
  relationshipLabel: string;
  /* ---- Corporates only; all empty on an individual --------------------- */
  /** Mainland, Freezone, or empty on a company not yet asked. */
  jurisdictionType: JurisdictionType | '';
  /** One of the seven, on a mainland company. */
  emirate: string;
  /** That key written out by the server, so no screen holds the list. */
  emirateLabel: string;
  /** The free zone's name, on a free zone company. */
  freezoneName: string;
  /**
   * What somebody typed when this was one free-text box.
   *
   * Read-only and usually empty: the API no longer accepts it, and answering
   * the structured question clears it. The form shows it so whoever is editing
   * an old record can see what it used to say.
   */
  jurisdiction: string;
  /**
   * The whole answer in one string - "Mainland · Dubai", "Freezone · DMCC".
   *
   * Composed by the server, and what every screen prints. It falls back to the
   * old free text, so nothing here has to know that field exists.
   */
  jurisdictionLabel: string;
  incorporatedOn: string | null;
  registryDocuments: RegistryDocument[];
  documentCount: number;
  expiring: ExpiringIdentityDocument[];
  createdAt: string;
}

/* ------------------------------------------------------------------------ *
 * Services, flows and jobs
 * ------------------------------------------------------------------------ */

/**
 * What reaching a stage means for the work.
 *
 * A tenant names their own stages, so nothing in the product can tell from
 * "Under maintenance" whether that job still needs somebody's attention. This
 * is the one thing they are asked to say about each stage, and every open
 * count, overdue figure and filter is built on it.
 */
export const StageKind = {
  OPEN: 'OPEN',
  DONE: 'DONE',
  CANCELLED: 'CANCELLED',
} as const;

export type StageKind = (typeof StageKind)[keyof typeof StageKind];

export const STAGE_KINDS = Object.values(StageKind);

/** How each is put to someone who has never heard the word "stage kind". */
export const STAGE_KIND_LABELS: Record<StageKind, string> = {
  OPEN: 'Still being worked on',
  DONE: 'Finished successfully',
  CANCELLED: 'Stopped or cancelled',
};

export function isClosedStage(kind: StageKind): boolean {
  return kind === StageKind.DONE || kind === StageKind.CANCELLED;
}

/** The palette a stage or a service may be given. Fixed, so boards stay legible. */
export const AccentColour = {
  SLATE: 'SLATE',
  BLUE: 'BLUE',
  TEAL: 'TEAL',
  GREEN: 'GREEN',
  AMBER: 'AMBER',
  RED: 'RED',
  VIOLET: 'VIOLET',
} as const;

export type AccentColour = (typeof AccentColour)[keyof typeof AccentColour];

export const ACCENT_COLOURS = Object.values(AccentColour);

export interface FlowStage {
  id: string;
  /** Stable. What jobs store, and what a rename does not touch. */
  key: string;
  label: string;
  kind: StageKind;
  colour: AccentColour;
  sortOrder: number;
  /** Jobs sitting here, so the editor can warn before removing it. */
  jobCount: number;
}

export interface Flow {
  id: string;
  name: string;
  description: string;
  /** The one new services fall back to. Exactly one per tenant. */
  isDefault: boolean;
  isActive: boolean;
  stages: FlowStage[];
  serviceCount: number;
  jobCount: number;
  createdAt: string;
}

/**
 * What goes on the firm's quotations, invoices and receipts.
 *
 * Kept apart from `HrSettings`, which holds the currency and the financial
 * year: those shape a payslip, these shape a tax invoice, and a firm can hand
 * its book-keeper one without handing them the other.
 */
/**
 * Where a quotation has got to.
 *
 * Expiry is not here: a sent, unanswered quotation past its date reads as
 * expired, worked out by the server against today rather than stored.
 */
export const QuotationStatus = {
  DRAFT: 'DRAFT',
  SENT: 'SENT',
  ACCEPTED: 'ACCEPTED',
  DECLINED: 'DECLINED',
} as const;

export type QuotationStatus = (typeof QuotationStatus)[keyof typeof QuotationStatus];

export interface QuotationLine {
  id: string;
  /** Where the figures came from, not what they mean. Null on a typed line. */
  serviceId: string | null;
  description: string;
  quantity: number;
  unitPriceMinor: number;
  vatRatePercent: number;
  lineSubtotalMinor: number;
  lineVatMinor: number;
  lineTotalMinor: number;
}

/**
 * An offer: what the firm will do, and what it will cost.
 *
 * Raised against a lead or a customer, and often both - `lead` is who it was
 * offered to and `customer` is who they became, so a converted enquiry's
 * quotation reads from either side.
 */
export interface Quotation {
  id: string;
  quoteCode: string;
  status: QuotationStatus;
  /** Sent, unanswered and past its date. Server-derived, never stored. */
  hasExpired: boolean;
  currency: string;
  /** Whether this document carries a tax column at all. Frozen on creation. */
  vatApplies: boolean;
  lead: { id: string; leadCode: string; name: string } | null;
  customer: { id: string; customerCode: string; name: string } | null;
  /** Who it was addressed to, as they stood when it was sent. */
  billTo: { name: string; address: string; trn: string; email: string };
  lines: QuotationLine[];
  subtotalMinor: number;
  vatMinor: number;
  totalMinor: number;
  issuedOn: string | null;
  validUntil: string | null;
  declinedReason: string;
  terms: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Where an invoice has got to.
 *
 * No "paid" among them: whether an invoice has been settled is derived from
 * what has been received against it, so these three are states somebody puts
 * it in and `paymentStatus` is worked out.
 */
export const InvoiceStatus = {
  DRAFT: 'DRAFT',
  ISSUED: 'ISSUED',
  CANCELLED: 'CANCELLED',
} as const;

export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

export const InvoicePaymentStatus = {
  UNPAID: 'UNPAID',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PAID: 'PAID',
} as const;

export type InvoicePaymentStatus = (typeof InvoicePaymentStatus)[keyof typeof InvoicePaymentStatus];

/**
 * A demand for payment. Customers only - a lead is not somebody you can bill.
 *
 * `invoiceCode` is empty until it is issued, which is the design rather than
 * an omission: a draft is not an invoice, and giving it a number would mean
 * either a hole in the series when it is deleted or that number going to a
 * later invoice.
 */
export interface Invoice {
  id: string;
  invoiceCode: string;
  status: InvoiceStatus;
  paymentStatus: InvoicePaymentStatus;
  /** Issued, unsettled and past its date. Server-derived, never stored. */
  isOverdue: boolean;
  daysOverdue: number;
  currency: string;
  vatApplies: boolean;
  customer: { id: string; customerCode: string; name: string } | null;
  quotation: { id: string; quoteCode: string } | null;
  billTo: { name: string; address: string; trn: string; email: string };
  /** The firm's own registration number, as it stood the day it was issued. */
  issuedByTrn: string;
  lines: QuotationLine[];
  subtotalMinor: number;
  vatMinor: number;
  totalMinor: number;
  paidAmountMinor: number;
  /** Never below zero, even where a customer has overpaid. */
  outstandingMinor: number;
  issuedOn: string | null;
  dueOn: string | null;
  cancelledOn: string | null;
  cancelledReason: string;
  terms: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

/** How a customer paid, for the receipt to say. */
export const PaymentMethod = {
  CASH: 'CASH',
  BANK_TRANSFER: 'BANK_TRANSFER',
  CHEQUE: 'CHEQUE',
  CARD: 'CARD',
  OTHER: 'OTHER',
} as const;

export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank transfer',
  CHEQUE: 'Cheque',
  CARD: 'Card',
  OTHER: 'Other',
};

/**
 * Money received against an invoice.
 *
 * `isPosted` is the whole of the two-step design: recording a receipt settles
 * the invoice, and posting it is what moves the accounts. Until then the
 * money is known about and the books have not heard.
 */
export interface Receipt {
  id: string;
  receiptCode: string;
  amountMinor: number;
  currency: string;
  receivedOn: string;
  method: PaymentMethod;
  reference: string;
  notes: string;
  invoice: { id: string; invoiceCode: string; totalMinor: number } | null;
  customer: { id: string; customerCode: string; name: string } | null;
  accountId: string;
  categoryKey: string;
  isPosted: boolean;
  postedOn: string | null;
  createdAt: string;
}

/** How late a debt is, in the bands a firm chases by. */
export interface AgeingBucket {
  key: 'CURRENT' | 'D1_30' | 'D31_60' | 'D61_90' | 'D90_PLUS';
  label: string;
  amountMinor: number;
  count: number;
}

/**
 * What the firm is owed - worked out from the invoices, not the accounts.
 *
 * The accounts are a cashbook: they record money that moved, and what is owed
 * is by definition money that has not.
 */
export interface ReceivablesSummary {
  currency: string;
  totalOutstandingMinor: number;
  invoiceCount: number;
  /** Of the total, how much is past its date. */
  overdueMinor: number;
  buckets: AgeingBucket[];
  topDebtors: {
    customerId: string;
    customerCode: string;
    name: string;
    outstandingMinor: number;
    oldestDaysOverdue: number;
  }[];
}

export interface StatementRow {
  id: string;
  date: string;
  kind: 'INVOICE' | 'RECEIPT';
  reference: string;
  description: string;
  chargedMinor: number;
  receivedMinor: number;
  /** Running, oldest first. What they owed after this line. */
  balanceMinor: number;
  /** Receipts only. Null on an invoice row. */
  isPosted: boolean | null;
}

export interface CustomerStatement {
  customer: { id: string; customerCode: string; name: string; trn: string };
  currency: string;
  rows: StatementRow[];
  totalChargedMinor: number;
  totalReceivedMinor: number;
  closingBalanceMinor: number;
  asAt: string;
}

/** What has been taken that the accounts have not heard about yet. */
export interface UnpostedReceipts {
  count: number;
  amountMinor: number;
}

/** What the services catalogue suggests a line should cost. */
export interface ServiceFee {
  id: string;
  name: string;
  defaultFeeMinor: number;
}

export interface BillingSettings {
  vatRegistered: boolean;
  trn: string;
  defaultVatRatePercent: number;
  paymentTermDays: number;
  quoteValidityDays: number;
  bankDetails: string;
  quoteTerms: string;
  invoiceTerms: string;
  /** Whether a valid tax invoice could be issued right now. Server-derived. */
  readyToInvoice: boolean;
}

export interface Service {
  id: string;
  serviceCode: string;
  name: string;
  description: string;
  colour: AccentColour;
  /** Minor units. Zero means the firm prices this one job by job. */
  defaultFeeMinor: number;
  isActive: boolean;
  sortOrder: number;
  flowId: string;
  flowName: string;
  /** Null until an icon is uploaded; the UI draws a chip instead. */
  iconUrl: string | null;
  jobCount: number;
  openJobCount: number;
  createdAt: string;
}

/** A service, customer or member of staff as a job refers to them. */
export interface JobParty {
  id: string;
  name: string;
  code: string;
}

/** Longest a job's own name may be. Matches the API. */
export const JOB_NAME_MAX_LENGTH = 160;

/** Longest one checklist line may be. Matches the API. */
export const JOB_CHECKLIST_TEXT_MAX_LENGTH = 200;

/** Longest the note carried with a handover may be. Matches the API. */
export const JOB_ASSIGNMENT_NOTE_MAX_LENGTH = 300;

/**
 * Somebody on the job, with the story of how they got there.
 *
 * Flat rather than a nested person, and the name may be empty: an employee
 * record can be deleted while the record of who handed the work over is still
 * worth reading.
 */
export interface JobAssignment {
  /** The assignment's own id, not the employee's. */
  id: string;
  employeeId: string;
  name: string;
  code: string;
  assignedAt: string;
  assignedById: string | null;
  /** Empty for work booked before assignments were recorded. */
  assignedByName: string;
  /** The note from the save that put this person on. One per handover. */
  note: string;
}

/** One line of what is left to do before a job can move. */
export interface JobChecklistItem {
  id: string;
  text: string;
  done: boolean;
  doneAt: string | null;
  doneById: string | null;
  doneByName: string;
  /**
   * Whoever the line falls to, where that differs from the job's own people.
   *
   * A list because an errand is routinely two people - one collects the
   * papers, the other walks them through the counter - and named rather than
   * left as ids, so a row can be drawn without a second request. A name is
   * empty where the employee record has since gone.
   */
  employees: JobParty[];
  dueOn: string | null;
  addedAt: string;
  addedById: string | null;
  addedByName: string;
}

export interface JobStage {
  key: string;
  label: string;
  kind: StageKind;
  colour: AccentColour;
  sortOrder: number;
}

export interface Job {
  id: string;
  jobCode: string;
  /** What the firm calls this piece of work. Empty for most jobs. */
  name: string;
  reference: string;
  service: (JobParty & { colour: AccentColour; iconUrl: string | null }) | null;
  customer: (JobParty & { kind: CustomerKind }) | null;
  stage: JobStage;
  flowId: string;
  assignees: JobParty[];
  startedOn: string;
  dueOn: string | null;
  closedAt: string | null;
  /** Negative once it is late. Null when nothing was ever agreed. */
  daysUntilDue: number | null;
  isOverdue: boolean;
  isDueSoon: boolean;
  notes: string;
  /**
   * How far through its checklist the job is.
   *
   * Two numbers rather than the items, so a list can draw "3/7" against forty
   * rows without carrying every line of every checklist to do it. The items
   * themselves are on the detail.
   */
  checklistDone: number;
  checklistTotal: number;
  createdAt: string;
  updatedAt: string;
}

export interface JobHistoryEntry {
  id: string;
  stageKey: string;
  stageLabel: string;
  movedAt: string;
  movedByName: string;
  note: string;
}

export interface JobDetail extends Job {
  /** The whole flow, so the page can draw a tracker. */
  stages: JobStage[];
  history: JobHistoryEntry[];
  /** The same people as `assignees`, with who put them there and when. */
  assignments: JobAssignment[];
  /** In stored order, which is the order the job's own people put it in. */
  checklist: JobChecklistItem[];
}

/** Which state of work a list is asking for. */
export const JobState = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  ALL: 'ALL',
} as const;

export type JobState = (typeof JobState)[keyof typeof JobState];

export interface JobSummary {
  open: number;
  overdue: number;
  dueSoon: number;
  closed: number;
}

/* ------------------------------------------------------------------------ *
 * Comments
 * ------------------------------------------------------------------------ */

/** Longest a single comment may be. Matches the API. */
export const COMMENT_MAX_LENGTH = 4000;

export interface Comment {
  id: string;
  /** Empty on a removed comment: the row stays, its words do not. */
  body: string;
  authorId: string | null;
  authorName: string;
  createdAt: string;
  /** Null unless it has been edited. Its presence is what shows "edited". */
  editedAt: string | null;
  editCount: number;
  isDeleted: boolean;
  deletedByName: string;
  /** Decided by the server, not inferred here from the role. */
  canEdit: boolean;
  canDelete: boolean;
  isMine: boolean;
}

export interface CommentRevision {
  id: string;
  body: string;
  replacedAt: string;
  editedByName: string;
}

/* ------------------------------------------------------------------------ *
 * Working time and leave
 * ------------------------------------------------------------------------ */

/** Days of the week as `Date.prototype.getDay` numbers them. Matches the API. */
export const WEEK_DAY_LABELS: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

export const LeaveAccrual = {
  ANNUAL_LUMP: 'ANNUAL_LUMP',
  MONTHLY_ACCRUAL: 'MONTHLY_ACCRUAL',
  NONE: 'NONE',
} as const;

export type LeaveAccrual = (typeof LeaveAccrual)[keyof typeof LeaveAccrual];

export const LEAVE_ACCRUAL_LABELS: Record<LeaveAccrual, string> = {
  ANNUAL_LUMP: 'Whole allowance at the start of the year',
  MONTHLY_ACCRUAL: 'A twelfth each month',
  NONE: 'No allowance - not counted',
};

export const DayHalf = {
  FIRST: 'FIRST',
  SECOND: 'SECOND',
} as const;

export type DayHalf = (typeof DayHalf)[keyof typeof DayHalf];

export interface LeaveTypeMaster {
  id: string;
  key: string;
  label: string;
  payPercent: number;
  annualDays: number;
  accrual: LeaveAccrual;
  countsWeekends: boolean;
  allowNegativeBalance: boolean;
  carryForward: boolean;
  carryForwardMaxDays: number;
  colour: AccentColour;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
  inUseCount: number;
}

export interface LeaveRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  leaveType: string;
  leaveTypeLabel: string;
  colour: AccentColour;
  from: string;
  to: string;
  startHalf: DayHalf | null;
  endHalf: DayHalf | null;
  days: number;
  payPercent: number;
  unpaidDays: number;
  reason: string;
  /** True once a finalised payslip has charged it - editing is refused. */
  locked: boolean;
  recordedAt: string;
}

export interface LeavePreview {
  days: number;
  unpaidDays: number;
  calendarDays: number;
  /** Days not charged because they fell on a weekend or a public holiday. */
  skippedDays: number;
  payPercent: number;
  leaveTypeLabel: string;
  countsWeekends: boolean;
}

export interface LeaveBalance {
  leaveType: string;
  label: string;
  colour: AccentColour;
  accrual: LeaveAccrual;
  entitled: number;
  carriedForward: number;
  taken: number;
  remaining: number;
  unlimited: boolean;
}

export interface LeaveBalanceSummary {
  employeeId: string;
  leaveYearLabel: string;
  leaveYearFrom: string;
  leaveYearTo: string;
  balances: LeaveBalance[];
}

export interface Holiday {
  id: string;
  name: string;
  date: string;
  isRecurring: boolean;
  notes: string;
}

/* ------------------------------------------------------------------------ *
 * Salary and payroll
 * ------------------------------------------------------------------------ */

export const SalaryComponentKind = {
  EARNING: 'EARNING',
  DEDUCTION: 'DEDUCTION',
} as const;

export type SalaryComponentKind = (typeof SalaryComponentKind)[keyof typeof SalaryComponentKind];

export const SalaryCalculation = {
  FIXED: 'FIXED',
  PERCENT_OF_BASIC: 'PERCENT_OF_BASIC',
} as const;

export type SalaryCalculation = (typeof SalaryCalculation)[keyof typeof SalaryCalculation];

/** The one component every structure must have. Matches the API. */
export const BASIC_COMPONENT_KEY = 'BASIC';

export interface SalaryComponentMaster {
  id: string;
  key: string;
  label: string;
  kind: SalaryComponentKind;
  calculation: SalaryCalculation;
  affectsDailyRate: boolean;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
  inUseCount: number;
}

export interface ResolvedSalaryLine {
  component: string;
  label: string;
  kind: SalaryComponentKind;
  calculation: SalaryCalculation;
  /** What was entered: minor units, or a percentage. */
  value: number;
  /** What it works out to in minor units. */
  amount: number;
  affectsDailyRate: boolean;
}

export interface ResolvedSalary {
  lines: ResolvedSalaryLine[];
  basic: number;
  gross: number;
  deductions: number;
  net: number;
  /** The part of gross that unpaid leave is charged against. */
  dailyRateBase: number;
}

export interface SalaryStructure {
  id: string;
  employeeId: string;
  effectiveFrom: string;
  note: string;
  salary: ResolvedSalary;
  isCurrent: boolean;
  createdAt: string;
}

export interface EmployeeSalary {
  current: { effectiveFrom: string; salary: ResolvedSalary } | null;
  history: SalaryStructure[];
}

export const DailyRateBasis = {
  FIXED_THIRTY: 'FIXED_THIRTY',
  CALENDAR_DAYS: 'CALENDAR_DAYS',
  WORKING_DAYS: 'WORKING_DAYS',
} as const;

export type DailyRateBasis = (typeof DailyRateBasis)[keyof typeof DailyRateBasis];

export const DAILY_RATE_BASIS_LABELS: Record<DailyRateBasis, string> = {
  FIXED_THIRTY: 'A flat 30 days every month',
  CALENDAR_DAYS: 'The days in that month',
  WORKING_DAYS: 'Working days only',
};

export const PayrollRunStatus = {
  DRAFT: 'DRAFT',
  FINALISED: 'FINALISED',
  CANCELLED: 'CANCELLED',
} as const;

export type PayrollRunStatus = (typeof PayrollRunStatus)[keyof typeof PayrollRunStatus];

export const PayslipPaymentStatus = {
  UNPAID: 'UNPAID',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PAID: 'PAID',
} as const;

export type PayslipPaymentStatus = (typeof PayslipPaymentStatus)[keyof typeof PayslipPaymentStatus];

export interface PayrollRun {
  id: string;
  year: number;
  month: number;
  period: string;
  periodLabel: string;
  status: PayrollRunStatus;
  currency: string;
  dailyRateBasis: DailyRateBasis;
  totalGross: number;
  totalDeductions: number;
  totalNet: number;
  totalPaid: number;
  outstanding: number;
  employeeCount: number;
  paidCount: number;
  notes: string;
  finalisedAt: string | null;
  createdAt: string;
}

export interface PayslipPayment {
  id: string;
  amount: number;
  paidOn: string;
  accountId: string;
  reference: string;
  recordedAt: string;
}

export interface Payslip {
  id: string;
  runId: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  period: string;
  periodLabel: string;
  currency: string;
  lines: { component: string; label: string; kind: SalaryComponentKind; amount: number }[];
  grossPay: number;
  deductions: number;
  unpaidLeaveDays: number;
  dailyRate: number;
  dailyRateDays: number;
  lossOfPay: number;
  /** Days of the month before they joined or after they left. */
  unworkedDays: number;
  unworkedDeduction: number;
  /** Unrecovered deductions brought in from the month before. */
  broughtForward: number;
  /** What this month could not take, waiting for the next. */
  carriedForward: number;
  adjustment: number;
  adjustmentNote: string;
  /** Never below zero - the remainder is in `carriedForward`. */
  netPay: number;
  paidAmount: number;
  paymentStatus: PayslipPaymentStatus;
  outstanding: number;
  payments: PayslipPayment[];
  notes: string;
  /** False while the run is a draft, so payment actions stay hidden. */
  runFinalised: boolean;
}

export interface HrSettings {
  currency: string;
  weekendDays: number[];
  /** The weekend written out by the server, so the UI owns no mapping. */
  weekendLabel: string;
  leaveYearStartMonth: number;
  /** The month the books are cut on. January in the UAE, and the default. */
  financialYearStartMonth: number;
  dailyRateBasis: DailyRateBasis;
  payDayOfMonth: number;
  allowPayrollWithoutStructure: boolean;
  payslipNote: string;
}

/* ------------------------------------------------------------------------ *
 * Accounts
 * ------------------------------------------------------------------------ */

export const AccountCategoryKind = {
  INCOME: 'INCOME',
  EXPENSE: 'EXPENSE',
} as const;

export type AccountCategoryKind = (typeof AccountCategoryKind)[keyof typeof AccountCategoryKind];

export const FinancialAccountKind = {
  CASH: 'CASH',
  BANK: 'BANK',
  CARD: 'CARD',
  OTHER: 'OTHER',
} as const;

export type FinancialAccountKind = (typeof FinancialAccountKind)[keyof typeof FinancialAccountKind];

export const FINANCIAL_ACCOUNT_KIND_LABELS: Record<FinancialAccountKind, string> = {
  CASH: 'Cash',
  BANK: 'Bank',
  CARD: 'Card',
  OTHER: 'Other',
};

export const TransactionKind = {
  INCOME: 'INCOME',
  EXPENSE: 'EXPENSE',
  TRANSFER: 'TRANSFER',
} as const;

export type TransactionKind = (typeof TransactionKind)[keyof typeof TransactionKind];

export const TRANSACTION_KIND_LABELS: Record<TransactionKind, string> = {
  INCOME: 'Money in',
  EXPENSE: 'Money out',
  TRANSFER: 'Transfer',
};

export const TransactionSource = {
  MANUAL: 'MANUAL',
  PAYROLL: 'PAYROLL',
  /** Posted from a receipt. Unwound by taking that receipt out of the books. */
  RECEIPT: 'RECEIPT',
} as const;

export type TransactionSource = (typeof TransactionSource)[keyof typeof TransactionSource];

/**
 * Where a row the books cannot edit came from.
 *
 * Only the two posted sources appear: a manual row is edited in place and
 * needs no label saying so.
 */
export const TRANSACTION_SOURCE_LABELS: Record<
  Exclude<TransactionSource, 'MANUAL'>,
  { label: string; title: string }
> = {
  PAYROLL: { label: 'Payroll', title: 'Posted by payroll. Reverse the payment on the payslip.' },
  RECEIPT: {
    label: 'Receipt',
    title: 'Posted from a receipt. Take that receipt out of the books.',
  },
};

export const TransactionPartyType = {
  CUSTOMER: 'CUSTOMER',
  EMPLOYEE: 'EMPLOYEE',
  OTHER: 'OTHER',
} as const;

export type TransactionPartyType = (typeof TransactionPartyType)[keyof typeof TransactionPartyType];

export interface AccountCategoryMaster {
  id: string;
  key: string;
  label: string;
  kind: AccountCategoryKind;
  description: string;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
  inUseCount: number;
}

export interface FinancialAccount {
  id: string;
  key: string;
  label: string;
  kind: FinancialAccountKind;
  bankName: string;
  accountNumber: string;
  openingBalance: number;
  openingBalanceOn: string | null;
  /** Opening balance plus everything since. Summed by the server. */
  balance: number;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
  inUseCount: number;
  /** An undated opening balance applies from here. */
  createdAt: string;
}

export interface Transaction {
  id: string;
  kind: TransactionKind;
  date: string;
  amount: number;
  category: string | null;
  categoryLabel: string | null;
  categoryKind: AccountCategoryKind | null;
  accountId: string;
  accountLabel: string;
  toAccountId: string | null;
  toAccountLabel: string | null;
  description: string;
  partyType: TransactionPartyType | null;
  partyId: string | null;
  partyName: string;
  reference: string;
  notes: string;
  source: TransactionSource;
  payslipId: string | null;
  /** False for a payroll-posted row, which must not be edited here. */
  editable: boolean;
  createdAt: string;
}

/* ------------------------------------------------------------------------ *
 * One party's account
 * ------------------------------------------------------------------------ */

/**
 * A line in a customer's or an employee's statement of account.
 *
 * Debit and credit are the party's side of the books, not the firm's: money
 * the firm paid out against them debits them, money received from them credits
 * them. A corporate-services firm pays a ministry on a customer's behalf and
 * is paid back separately, so the debit column is where the customer's own
 * position is stated - not a rarity to be explained away.
 */
export interface PartyLedgerEntry {
  id: string;
  date: string;
  /** The ledger column's own name for a description. */
  particulars: string;
  reference: string;
  categoryLabel: string | null;
  accountLabel: string;
  /** Minor units. Exactly one of the two is non-zero. */
  debit: number;
  credit: number;
  /** Running balance after this line. Positive is a debit balance. */
  balance: number;
  source: TransactionSource;
}

export interface PartyLedgerTotals {
  debit: number;
  credit: number;
  /** debit - credit. Positive is owed to the firm. */
  balance: number;
  count: number;
  firstOn: string | null;
  lastOn: string | null;
}

export interface PartyLedger {
  partyType: TransactionPartyType;
  partyId: string;
  name: string;
  currency: string;
  /**
   * Whether a balance carries from one period into the next.
   *
   * True for a customer, whose debt survives a year end. False for an
   * employee: a salary paid last year is not owed into this one, so their
   * statement reports the period rather than a position.
   */
  carriesBalance: boolean;
  /** The period on screen. Null dates mean everything ever recorded. */
  label: string;
  from: string | null;
  to: string | null;
  openingBalance: number;
  debit: number;
  credit: number;
  closingBalance: number;
  entries: PartyLedgerEntry[];
  /** Every financial year this party has a movement in, newest first. */
  years: { startYear: number; label: string }[];
  allTime: PartyLedgerTotals;
}

export interface CategoryTotal {
  category: string;
  label: string;
  kind: AccountCategoryKind;
  total: number;
  count: number;
}

export interface PeriodSummary {
  from: string;
  to: string;
  label: string;
  currency: string;
  income: number;
  expense: number;
  net: number;
  transactionCount: number;
  byCategory: CategoryTotal[];
}

export interface MonthPoint {
  period: string;
  label: string;
  income: number;
  expense: number;
  net: number;
}

export interface YearTotals {
  label: string;
  startYear: number;
  income: number;
  expense: number;
  net: number;
}

export interface AccountsOverview {
  currency: string;
  accounts: FinancialAccount[];
  totalBalance: number;
  thisMonth: PeriodSummary;
  /** The financial year so far. */
  yearToDate: YearTotals;
  trend: MonthPoint[];
}

/** Where one account stood at both ends of a year. */
export interface AccountYearPosition {
  id: string;
  label: string;
  kind: FinancialAccountKind;
  isActive: boolean;
  opening: number;
  moneyIn: number;
  moneyOut: number;
  /** opening + moneyIn - moneyOut, which is next year's opening. */
  closing: number;
}

/** One financial year: its totals, its months, and the year before it. */
export interface FinancialYearView {
  label: string;
  startYear: number;
  from: string;
  to: string;
  summary: PeriodSummary;
  months: MonthPoint[];
  previous: YearTotals | null;
  accounts: AccountYearPosition[];
  /** What the books opened and closed the year holding. */
  openingBalance: number;
  closingBalance: number;
}

/**
 * The windows the accounts pages can ask for.
 *
 * Resolved on the server, which is the only side that knows which month the
 * financial year starts in.
 */
export const AccountsPeriod = {
  MONTH: 'month',
  QUARTER: 'quarter',
  FINANCIAL_YEAR: 'financial-year',
} as const;
export type AccountsPeriod = (typeof AccountsPeriod)[keyof typeof AccountsPeriod];

export const ACCOUNTS_PERIOD_LABELS: Record<AccountsPeriod, string> = {
  [AccountsPeriod.MONTH]: 'This month',
  [AccountsPeriod.QUARTER]: 'This quarter',
  [AccountsPeriod.FINANCIAL_YEAR]: 'This financial year',
};

/** The people-and-money line on the tenant dashboard. Null for a non-admin. */
/**
 * The people-and-money figures, cut by what the reader may see.
 *
 * Three sections rather than one flat object, because the three come from
 * three different permissions and a flat shape cannot say "you may see the
 * payroll but not the takings". A section is null when its subject is closed
 * to the reader, and the server does not run the queries behind it either.
 *
 * The whole thing is null when none of the three is open.
 */
export interface HrSnapshot {
  /** Always present - a screen that cannot read it prints the wrong symbol. */
  currency: string;
  leave: { onLeaveToday: number } | null;
  payroll: {
    openRun: {
      id: string;
      period: string;
      periodLabel: string;
      status: PayrollRunStatus;
    } | null;
    unpaidPayslips: number;
    unpaidAmount: number;
  } | null;
  money: {
    monthIncome: number;
    monthExpense: number;
    monthNet: number;
    cashOnHand: number;
  } | null;
}

/* ------------------------------------------------------------------------ *
 * The customer portal
 * ------------------------------------------------------------------------ */

/**
 * What a new customer login lasts, unless whoever creates it says otherwise.
 * Mirrors GUEST_ACCESS_DAYS on the server, which is what actually decides.
 */
export const GUEST_ACCESS_DAYS = 30;

/** The server refuses anything longer; the box stops typing at it. */
export const JOB_REMARK_MAX_LENGTH = 2000;

/** And the line the firm types when it turns a customer's file away. */
export const PORTAL_DECLINE_REASON_MAX_LENGTH = 500;

/**
 * Which side of the conversation wrote a remark.
 *
 * Mirrors RemarkAuthorKind on the server. The thread is one list with both
 * sides in it, so this is the only thing a row has to go on - without it the
 * customer's own question renders under the firm's name.
 */
export const RemarkAuthorKind = {
  FIRM: 'FIRM',
  CUSTOMER: 'CUSTOMER',
} as const;

export type RemarkAuthorKind = (typeof RemarkAuthorKind)[keyof typeof RemarkAuthorKind];

/** Where a file the customer sent has got to. Mirrors PortalUploadState. */
export const PortalUploadState = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  DECLINED: 'DECLINED',
} as const;

export type PortalUploadState = (typeof PortalUploadState)[keyof typeof PortalUploadState];

/* The limits a guest uploads under, copied from `portal-upload.service.ts`
   so the screen can state them before anything is picked. The server is
   still what refuses - these numbers only exist so that a person on a phone
   learns the rule before a slow upload rather than after it. */

/** GUEST_UPLOAD_MIME_TYPE_LIST, as the file picker's `accept` wants it. */
export const PORTAL_UPLOAD_ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp';

/** PORTAL_UPLOAD_MAX_MB - one file. Smaller than the firm's own allowance. */
export const PORTAL_UPLOAD_MAX_MB = 10;

/** PORTAL_UPLOAD_MAX_PENDING - files waiting on one job at once. */
export const PORTAL_UPLOAD_MAX_PENDING = 10;

/** PORTAL_UPLOAD_MAX_PENDING_MB - what those waiting files may weigh together. */
export const PORTAL_UPLOAD_MAX_PENDING_MB = 40;

/** One job's customer login, as the firm's side sees it. Never the password. */
export interface JobAccess {
  username: string;
  isActive: boolean;
  expiresAt: string;
  /** True once the date has passed, whatever the flag says. */
  hasExpired: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

/**
 * The answer to creating a login, and the only sighting of the password.
 *
 * Deliberately a type of its own rather than a field on {@link JobAccess}:
 * the two are not the same thing, and a password that could be optional on
 * the ordinary view is a password something will eventually try to render
 * from a cache that no longer has it.
 */
export interface JobAccessCreated {
  access: JobAccess;
  password: string;
}

/**
 * One line of the conversation about a job - from either side.
 *
 * The same shape both sides are served, because there is nothing on a remark
 * the customer may not see: it was written to be read by them. Which is why
 * the guest's page and the firm's panel render from this one type.
 */
export interface JobRemark {
  id: string;
  body: string;
  /** The firm, or the customer following the job. */
  authorKind: RemarkAuthorKind;
  authorName: string;
  createdAt: string;
  /** Null unless somebody has changed the wording since. */
  editedAt: string | null;
}

/** A row of the customer's file, with the tick that decides what they see. */
export interface JobPortalDocument {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  canPreview: boolean;
  /** Shared *for this job*. The customer's other job is a separate answer. */
  sharedWithGuest: boolean;
  createdAt: string;
}

/* The guest's own side. Narrower than the types above on purpose - this is
   everything the portal payload carries, and nothing here has a field the
   customer may not read. */

export interface PortalStage {
  key: string;
  label: string;
  kind: StageKind;
  colour: AccentColour;
  sortOrder: number;
}

export interface PortalHistoryEntry {
  id: string;
  stageLabel: string;
  movedAt: string;
}

export interface PortalJob {
  /** What the firm calls this piece of work, or empty. */
  name: string;
  jobCode: string;
  serviceName: string;
  customerName: string;
  stage: PortalStage;
  /** The whole flow in order, so the stage reads as a position. */
  stages: PortalStage[];
  startedOn: string;
  dueOn: string | null;
  closedAt: string | null;
  isClosed: boolean;
  updatedAt: string;
  /** Where the work has been, newest first. Stage and day only. */
  history: PortalHistoryEntry[];
}

export interface PortalDocument {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  canPreview: boolean;
  sharedAt: string;
}

/**
 * A file the customer sent in, as the customer is shown it.
 *
 * Nothing here about the scanners, who at the firm decided, or what document
 * it became. The server's guest view is the base and the firm's extends it,
 * so a field added for the firm's screen cannot reach this one by accident -
 * and the same is true here.
 */
export interface PortalUpload {
  id: string;
  /** The name it was sent under. */
  name: string;
  mimeType: string;
  sizeBytes: number;
  /** Whether the in-app viewer can render it, or it must be downloaded. */
  canPreview: boolean;
  state: PortalUploadState;
  sentAt: string;
  /** When the firm accepted or declined it, or null while it waits. */
  decidedAt: string | null;
  /** What the firm said when they did not take it. Empty otherwise. */
  declineReason: string;
}

/** The same file with what only the firm needs on it. */
export interface PortalUploadReview extends PortalUpload {
  /** The customer, as the portal knows them. */
  sentByName: string;
  /** Who at the firm decided, as they were called at the time. */
  decidedByName: string;
  scanVerdict: string;
  scanFindings: string[];
  /** The document it became, once accepted. */
  documentId: string | null;
}

/** The whole portal page, in one payload - the server assembles it in one go. */
export interface PortalView {
  job: PortalJob;
  remarks: JobRemark[];
  documents: PortalDocument[];
  /** What this customer has sent in, and what became of each one. */
  uploads: PortalUpload[];
}

/* ------------------------------------------------------------------------ *
 * Leads - the firm's own pipeline, before anybody is a customer
 * ------------------------------------------------------------------------ */

/**
 * What reaching a stage means for the lead in it.
 *
 * On the tenant's master rather than decided here, for the reason a
 * relationship type's side is: a firm renames its pipeline and adds stages of
 * its own, so the only thing this app can safely know is the handful of
 * meanings behind them. Every screen branches on this and never on a key.
 */
export const LeadOutcome = {
  /** Still in play. */
  OPEN: 'OPEN',
  /** They signed - this is the only state a lead can be converted from. */
  WON: 'WON',
  /** They did not. */
  LOST: 'LOST',
} as const;

export type LeadOutcome = (typeof LeadOutcome)[keyof typeof LeadOutcome];

/** One entry in a tenant's lead-stage master. */
export interface LeadStageMaster {
  id: string;
  /** Stable. What leads store, and what a rename does not touch. */
  key: string;
  label: string;
  description: string;
  outcome: LeadOutcome;
  colour: AccentColour;
  isActive: boolean;
  /** Seeded with the tenant; renameable and retirable, not deletable. */
  isSystem: boolean;
  sortOrder: number;
  /** How many leads sit in it, so a warning can be shown before retiring. */
  inUseCount: number;
}

/** One entry in a tenant's lead-source master. */
export interface LeadSourceMaster {
  id: string;
  key: string;
  label: string;
  description: string;
  colour: AccentColour;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
  inUseCount: number;
}

/** A stage or source as a row renders it, resolved by the server. */
export interface LeadLabel {
  key: string;
  label: string;
  colour: AccentColour;
}

/**
 * An enquiry.
 *
 * Its own record rather than a customer with a stage on it: a lead has no
 * licence, no document shelf and no files. What it carries instead is enough
 * of a customer's fields, under the same names, that converting is a copy
 * rather than a form to fill in twice.
 */
export interface Lead {
  id: string;
  leadCode: string;
  /** Which sort of customer this would become. */
  kind: CustomerKind;
  name: string;
  email: string;
  phone: string;
  whatsapp: string;
  address: string;

  stage: LeadLabel | null;
  /** What the stage means. Screens branch on this, never on `stage.key`. */
  outcome: LeadOutcome;
  stageChangedOn: string;
  /** Whole days it has sat where it is, counted in the reader's timezone. */
  daysInStage: number;
  /** Every stage it has been through, oldest first. */
  stageHistory: { stageKey: string; label: string; enteredOn: string }[];

  source: LeadLabel | null;

  /** The colleague chasing it, from the staff list. */
  owner: { id: string; name: string } | null;
  /** What they asked to be quoted for. */
  services: { id: string; name: string }[];

  /** Fils, like every amount in here. A forecast until it is a job. */
  estimatedValueMinor: number;
  expectedCloseOn: string | null;
  nextFollowUpOn: string | null;
  lastContactedOn: string | null;
  /** The promised call-back has gone by, and the lead is still open. */
  followUpOverdue: boolean;

  notes: string;

  nationality: string;
  /** The country's name from the tenant's master. */
  nationalityLabel: string;
  dateOfBirth: string | null;

  jurisdictionType: JurisdictionType | '';
  emirate: Emirate | '';
  freezoneName: string;
  /** "Mainland · Dubai" or the free zone's name, built the customer's way. */
  jurisdictionLabel: string;
  incorporatedOn: string | null;

  /** Only meaningful while the stage means lost; cleared when it reopens. */
  lostReason: string;

  /**
   * The customer it became.
   *
   * Set once, by conversion, and the reason the lead is kept rather than
   * transformed. A converted lead is read-only.
   */
  convertedCustomer: { id: string; name: string; customerCode: string } | null;
  convertedOn: string | null;

  createdAt: string;
  updatedAt: string;
}

/** One column of the pipeline board. */
export interface LeadPipelineColumn {
  key: string;
  label: string;
  colour: AccentColour;
  outcome: LeadOutcome;
  sortOrder: number;
  count: number;
  /** The sum of the forecasts in this stage, in fils. */
  valueMinor: number;
}

/**
 * The board, and the currency its figures are in.
 *
 * The currency rides along rather than being fetched separately, the way the
 * accounts summary carries its own: the settings endpoint that holds it is
 * behind a different permission, so a colleague granted the pipeline and
 * nothing else would have had every amount fall back to a guess.
 */
export interface LeadPipeline {
  currency: string;
  columns: LeadPipelineColumn[];
}

/** The pickers the pipeline screen needs, in one call. */
export interface LeadMeta {
  stages: LeadStageMaster[];
  sources: LeadSourceMaster[];
}

/* ------------------------------------------------------------------------ *
 * Exports
 * ------------------------------------------------------------------------ */

/**
 * The datasets a firm can pull out as a spreadsheet. Mirrors the backend list;
 * the frontend only ever names one of these when it starts an export.
 */
export const ExportDataset = {
  INDIVIDUALS: 'INDIVIDUALS',
  CORPORATES: 'CORPORATES',
  LEADS: 'LEADS',
  QUOTATIONS: 'QUOTATIONS',
  INVOICES: 'INVOICES',
  JOBS: 'JOBS',
  FINANCE: 'FINANCE',
} as const;

export type ExportDataset = (typeof ExportDataset)[keyof typeof ExportDataset];

/** What a firm calls each one, for the button and the tray. */
export const EXPORT_DATASET_LABELS: Record<ExportDataset, string> = {
  INDIVIDUALS: 'Individual customers',
  CORPORATES: 'Corporate customers',
  LEADS: 'Leads',
  QUOTATIONS: 'Quotations',
  INVOICES: 'Invoices',
  JOBS: 'Jobs',
  FINANCE: 'Finance',
};

/** Where a job is in its life. READY has a file to save; FAILED has a reason. */
export const ExportStatus = {
  QUEUED: 'QUEUED',
  PROCESSING: 'PROCESSING',
  READY: 'READY',
  FAILED: 'FAILED',
} as const;

export type ExportStatus = (typeof ExportStatus)[keyof typeof ExportStatus];
